import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  LayoutDashboard, Image as ImageIcon, Users, Settings, LogOut, Plus, Trash2, 
  Edit, Save, X, Upload, Search, Sparkles, FolderOpen, Tag, Palette, Layout, Grid, Download,
  Star, Gift, Utensils
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AdminAppsManager from './AdminAppsManager';
import AdminUICustomizer from './AdminUICustomizer';
import ReviewManager from './ReviewManager';
import CouponManager from './CouponManager';
import DigiHostess from './DigiHostess';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// User Management Component
function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    loadUsers();
  }, [searchTerm]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API}/admin/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      
      setUsers(response.data.users || []);
      setTotalUsers(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      businessName: user.businessName || '',
      businessCategory: user.businessCategory || '',
      businessLocation: user.businessLocation || '',
      address: user.address || '',
      zipcode: user.zipcode || '',
      about: user.about || '',
      profilePicUrl: user.profilePicUrl || '',
      logoUrl: user.logoUrl || ''
    });
    setShowEditModal(true);
  };

  const saveUser = async () => {
    if (!selectedUser) return;
    
    try {
      await axios.put(
        `${API}/admin/users/${selectedUser.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } }
      );
      
      toast.success('User updated successfully!');
      setShowEditModal(false);
      loadUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error('Failed to update user');
    }
  };

  const exportToCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/export`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Users exported successfully!');
    } catch (error) {
      console.error('Failed to export users:', error);
      toast.error('Failed to export users');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
          <p className="text-gray-500 mt-1">Manage all registered users ({totalUsers} total)</p>
        </div>
        <Button onClick={exportToCSV} className="bg-green-600 hover:bg-green-700">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search users by name, email, or business..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Business</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-semibold">
                            {user.name?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{user.name || 'N/A'}</p>
                            <p className="text-xs text-gray-500">{user.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{user.email || 'N/A'}</td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{user.businessName || 'N/A'}</p>
                          <p className="text-xs text-gray-500">{user.businessCategory || ''}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{user.businessLocation || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          onClick={() => openEditModal(user)}
                          variant="outline"
                          size="sm"
                          className="text-purple-600 hover:bg-purple-50"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full shadow-2xl max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Edit User</h3>
                <p className="text-sm text-gray-500">Update user information</p>
              </div>
              <Button onClick={() => setShowEditModal(false)} variant="ghost" size="sm">
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Name *</label>
                  <Input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    placeholder="Full name"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Email *</label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Business Name</label>
                  <Input
                    type="text"
                    value={editForm.businessName}
                    onChange={(e) => setEditForm({ ...editForm, businessName: e.target.value })}
                    placeholder="Business name"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Business Category</label>
                  <Input
                    type="text"
                    value={editForm.businessCategory}
                    onChange={(e) => setEditForm({ ...editForm, businessCategory: e.target.value })}
                    placeholder="e.g., Restaurant, Tech"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Business Location</label>
                  <Input
                    type="text"
                    value={editForm.businessLocation}
                    onChange={(e) => setEditForm({ ...editForm, businessLocation: e.target.value })}
                    placeholder="e.g., Honolulu"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Zipcode</label>
                  <Input
                    type="text"
                    value={editForm.zipcode}
                    onChange={(e) => setEditForm({ ...editForm, zipcode: e.target.value })}
                    placeholder="Zipcode"
                  />
                </div>

                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700 block mb-2">Address</label>
                  <Input
                    type="text"
                    value={editForm.address}
                    onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    placeholder="Full address"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Profile Picture URL</label>
                  <Input
                    type="text"
                    value={editForm.profilePicUrl}
                    onChange={(e) => setEditForm({ ...editForm, profilePicUrl: e.target.value })}
                    placeholder="https://example.com/profile.jpg"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Logo URL</label>
                  <Input
                    type="text"
                    value={editForm.logoUrl}
                    onChange={(e) => setEditForm({ ...editForm, logoUrl: e.target.value })}
                    placeholder="https://example.com/logo.png"
                  />
                </div>

                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700 block mb-2">About</label>
                  <textarea
                    value={editForm.about}
                    onChange={(e) => setEditForm({ ...editForm, about: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows="4"
                    placeholder="About the user/business"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3 sticky bottom-0 bg-white">
              <Button
                onClick={() => setShowEditModal(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={saveUser}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    thumbnail: '',
    frameImage: '',
    category: 'business',
    textColor: '#000000',
    bgColor: 'transparent'
  });

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken) {
      navigate('/admin/login');
    } else {
      loadTemplates();
    }
  }, [navigate]);

  const loadTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/templates`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const addTemplate = async () => {
    if (!newTemplate.name || !newTemplate.frameImage) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/templates`,
        newTemplate,
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } }
      );
      toast.success('Template added successfully!');
      setShowAddModal(false);
      setNewTemplate({
        name: '',
        thumbnail: '',
        frameImage: '',
        category: 'business',
        textColor: '#000000',
        bgColor: 'transparent'
      });
      loadTemplates();
    } catch (error) {
      console.error('Failed to add template:', error);
      toast.error('Failed to add template');
    }
  };

  const deleteTemplate = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await axios.delete(`${API}/admin/templates/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
        });
        toast.success('Template deleted!');
        loadTemplates();
      } catch (error) {
        console.error('Failed to delete template:', error);
        toast.error('Failed to delete template');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-64 bg-slate-900 text-white flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Admin Panel</h1>
              <p className="text-xs text-gray-400">Design Suite Pro</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'dashboard' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-slate-800'
            }`}
          >
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </button>

          <button
            onClick={() => setActiveTab('apps')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'apps' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-slate-800'
            }`}
          >
            <Grid className="w-5 h-5" />
            <span>Apps</span>
          </button>

          <button
            onClick={() => setActiveTab('ui')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'ui' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-slate-800'
            }`}
          >
            <Palette className="w-5 h-5" />
            <span>UI Customizer</span>
          </button>

          <button
            onClick={() => setActiveTab('templates')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'templates' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-slate-800'
            }`}
          >
            <FolderOpen className="w-5 h-5" />
            <span>Templates</span>
          </button>

          <button
            onClick={() => setActiveTab('navigation')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'navigation' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-slate-800'
            }`}
          >
            <Layout className="w-5 h-5" />
            <span>Navigation</span>
          </button>

          <button
            onClick={() => setActiveTab('users')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'users' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-slate-800'
            }`}
          >
            <Users className="w-5 h-5" />
            <span>Users</span>
          </button>

          <button
            onClick={() => setActiveTab('review-manager')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'review-manager' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-slate-800'
            }`}
          >
            <Star className="w-5 h-5" />
            <span>Review Manager</span>
          </button>

          <button
            onClick={() => setActiveTab('coupon-manager')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'coupon-manager' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-slate-800'
            }`}
          >
            <Gift className="w-5 h-5" />
            <span>Coupon Manager</span>
          </button>

          <button
            onClick={() => setActiveTab('digi-hostess')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'digi-hostess' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-slate-800'
            }`}
          >
            <Utensils className="w-5 h-5" />
            <span>DigiHostess</span>
          </button>

          <button
            onClick={() => setActiveTab('settings')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'settings' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-slate-800'
            }`}
          >
            <Settings className="w-5 h-5" />
            <span>Settings</span>
          </button>
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-slate-700">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-900/20 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {activeTab === 'dashboard' && 'Dashboard'}
                {activeTab === 'apps' && 'Apps Management'}
                {activeTab === 'ui' && 'UI Customizer'}
                {activeTab === 'templates' && 'Templates Management'}
                {activeTab === 'navigation' && 'Navigation Management'}
                {activeTab === 'users' && 'Users Management'}
                {activeTab === 'review-manager' && 'Review & Rewards Manager'}
                {activeTab === 'coupon-manager' && 'Coupon Manager'}
                {activeTab === 'digi-hostess' && 'DigiHostess - Restaurant Service'}
                {activeTab === 'settings' && 'Settings'}
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                {activeTab === 'dashboard' && 'Overview of your platform'}
                {activeTab === 'apps' && 'Configure and customize all applications'}
                {activeTab === 'ui' && 'Customize website appearance and theme'}
                {activeTab === 'templates' && 'Manage business info templates'}
                {activeTab === 'navigation' && 'Edit navigation menu and links'}
                {activeTab === 'users' && 'Manage user accounts'}
                {activeTab === 'review-manager' && 'Manage businesses, reviews, and customer rewards'}
                {activeTab === 'coupon-manager' && 'Manage coupon codes and redemption limits'}
                {activeTab === 'digi-hostess' && 'Restaurant service orchestration and table management'}
                {activeTab === 'settings' && 'Platform settings and configuration'}
              </p>
            </div>
            {activeTab === 'templates' && (
              <Button
                onClick={() => setShowAddModal(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Template
              </Button>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="p-8">
          {activeTab === 'dashboard' && (
            <div className="grid grid-cols-4 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <FolderOpen className="w-12 h-12 mx-auto text-purple-600 mb-2" />
                    <p className="text-3xl font-bold text-gray-900">{templates.length}</p>
                    <p className="text-sm text-gray-500">Templates</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <ImageIcon className="w-12 h-12 mx-auto text-blue-600 mb-2" />
                    <p className="text-3xl font-bold text-gray-900">0</p>
                    <p className="text-sm text-gray-500">Custom Images</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <Users className="w-12 h-12 mx-auto text-green-600 mb-2" />
                    <p className="text-3xl font-bold text-gray-900">0</p>
                    <p className="text-sm text-gray-500">Active Users</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <Sparkles className="w-12 h-12 mx-auto text-pink-600 mb-2" />
                    <p className="text-3xl font-bold text-gray-900">0</p>
                    <p className="text-sm text-gray-500">Designs Created</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'templates' && (
            <div>
              {templates.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-4">No templates yet</p>
                  <Button onClick={() => setShowAddModal(true)} className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Template
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-6">
                  {templates.map(template => (
                    <Card key={template.id} className="overflow-hidden">
                      <div className="aspect-video bg-gray-100">
                        <img 
                          src={template.thumbnail || template.frameImage} 
                          alt={template.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-1">{template.name}</h3>
                        <p className="text-xs text-gray-500 mb-3">{template.category}</p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" className="flex-1">
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => deleteTemplate(template.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'apps' && <AdminAppsManager />}

          {activeTab === 'ui' && <AdminUICustomizer />}

          {activeTab === 'navigation' && (
            <div className="text-center py-12">
              <Layout className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Navigation management coming soon</p>
            </div>
          )}

          {activeTab === 'users' && <UserManagement />}

          {activeTab === 'review-manager' && <ReviewManager />}

          {activeTab === 'coupon-manager' && <CouponManager />}

          {activeTab === 'digi-hostess' && <DigiHostess />}

          {activeTab === 'settings' && (
            <div className="text-center py-12">
              <Settings className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Settings coming soon</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Template Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Add New Template</h3>
                <p className="text-sm text-gray-500">Create a new business info template</p>
              </div>
              <Button onClick={() => setShowAddModal(false)} variant="ghost" size="sm">
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Template Name *</label>
                <Input
                  type="text"
                  placeholder="e.g., Modern Blue Frame"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Category *</label>
                <select
                  value={newTemplate.category}
                  onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="business">Business</option>
                  <option value="creative">Creative</option>
                  <option value="elegant">Elegant</option>
                  <option value="modern">Modern</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Frame Image URL *</label>
                <Input
                  type="text"
                  placeholder="https://example.com/frame.png"
                  value={newTemplate.frameImage}
                  onChange={(e) => setNewTemplate({ ...newTemplate, frameImage: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">Direct URL to the decorative frame image</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Thumbnail URL (optional)</label>
                <Input
                  type="text"
                  placeholder="https://example.com/thumbnail.png"
                  value={newTemplate.thumbnail}
                  onChange={(e) => setNewTemplate({ ...newTemplate, thumbnail: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty to use frame image</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Text Color</label>
                  <Input
                    type="color"
                    value={newTemplate.textColor}
                    onChange={(e) => setNewTemplate({ ...newTemplate, textColor: e.target.value })}
                    className="h-10"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Background Color</label>
                  <Input
                    type="color"
                    value={newTemplate.bgColor === 'transparent' ? '#ffffff' : newTemplate.bgColor}
                    onChange={(e) => setNewTemplate({ ...newTemplate, bgColor: e.target.value })}
                    className="h-10"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <Button
                onClick={() => setShowAddModal(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={addTemplate}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                <Save className="w-4 h-4 mr-2" />
                Add Template
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
